import React from 'react';
import styled from 'styled-components';
import { width as styledSystemWidth } from 'styled-system';
import { Box, TextBody } from '@ally/metronome-ui';
import { TextInput } from '../inputs';
import { containsNonEnglishCharacters, invalidStrings } from '../validations';
import { useFormsContext } from '../context';
const MAX_LENGTH = 55;
const SECURITY_Q_A_REGEX = /^[a-zA-Z0-9\s?,.!'\-:;]+$/;
const EMOJI_REGEX = /([\u2700-\u27BF]|[\uE000-\uF8FF]|\uD83C[\uDC00-\uDFFF]|\uD83D[\uDC00-\uDFFF]|[\u2011-\u26FF]|\uD83E[\uDD10-\uDDFF])/g;
const MULTIPLE_CONTIGUOUS_SPACES_REGEX = /\s{2}/g;
export const ERROR_QUESTION_REQUIRED_MESSAGE = 'Enter security question to continue.';
export const ERROR_QUESTION_MAX_LENGTH_MESSAGE = `Your security question can’t contain more than ${MAX_LENGTH} characters.`;
export const ERROR_QUESTION_PATTERN_MESSAGE = `Your security question can't contain emoji, foreign language characters, more than one space in a row, or special characters like ^ * ( ) \\ |`;
export const ERROR_QUESTION_CANNOT_MATCH_MESSAGE = `Your security question can't match your answer.`;
export const ERROR_ANSWER_REQUIRED_MESSAGE = 'Enter answer to continue.';
export const ERROR_ANSWER_MAX_LENGTH_MESSAGE = `Your answer can’t contain more than ${MAX_LENGTH} characters.`;
export const ERROR_ANSWER_PATTERN_MESSAGE = `Your answer can't contain emoji, foreign language characters, more than one space in a row, or special characters like ^ * ( ) \\ |`;
export const ERROR_ANSWER_CANNOT_MATCH_MESSAGE = `Your answer can't match your security question.`;
const StyledTextInput = styled(TextInput)(styledSystemWidth);
const SecurityQuestionGroup = ({ answerName, badWordsObject, questionName, inputFieldWidth, required = false, validateAnswer, validateQuestion, className, hideSubCopy = false, }) => {
    const invalidStringsValidator = invalidStrings.validator(badWordsObject);
    const formContext = useFormsContext();
    const fieldsMatch = () => {
        const formValues = formContext.values;
        const { [questionName]: question, [answerName]: answer } = formValues;
        if (!question || !answer) {
            return false;
        }
        if (question === answer) {
            return true;
        }
        return false;
    };
    const defaultValidateQuestion = (value) => {
        // if the input is not required then an empty field should never show a validation error
        if (!required && !value) {
            return undefined;
        }
        if (!value) {
            return ERROR_QUESTION_REQUIRED_MESSAGE;
        }
        if (value.length > MAX_LENGTH) {
            return ERROR_QUESTION_MAX_LENGTH_MESSAGE;
        }
        if (!SECURITY_Q_A_REGEX.test(value) ||
            MULTIPLE_CONTIGUOUS_SPACES_REGEX.test(value) ||
            containsNonEnglishCharacters(value) ||
            EMOJI_REGEX.test(value)) {
            return ERROR_QUESTION_PATTERN_MESSAGE;
        }
        if (!invalidStringsValidator(value)) {
            return invalidStrings.errorMessage;
        }
        if (fieldsMatch()) {
            return ERROR_QUESTION_CANNOT_MATCH_MESSAGE;
        }
        return undefined;
    };
    const defaultValidateAnswer = (value) => {
        // if the input is not required then an empty field should never show a validation error
        if (!required && !value) {
            return undefined;
        }
        if (!value) {
            return ERROR_ANSWER_REQUIRED_MESSAGE;
        }
        if (value.length > MAX_LENGTH) {
            return ERROR_ANSWER_MAX_LENGTH_MESSAGE;
        }
        if (!SECURITY_Q_A_REGEX.test(value) ||
            MULTIPLE_CONTIGUOUS_SPACES_REGEX.test(value) ||
            containsNonEnglishCharacters(value) ||
            EMOJI_REGEX.test(value)) {
            return ERROR_ANSWER_PATTERN_MESSAGE;
        }
        if (!invalidStringsValidator(value)) {
            return invalidStrings.errorMessage;
        }
        if (fieldsMatch()) {
            return ERROR_ANSWER_CANNOT_MATCH_MESSAGE;
        }
        return undefined;
    };
    return (React.createElement(Box, { className: className },
        !hideSubCopy && (React.createElement(Box, { mb: "sm" },
            React.createElement(TextBody, { tag: "p", size: "sm", "data-testid": "security-question-group-subcopy" }, "We'll ask you to answer your Security Question when you call us or if you forget your password."))),
        React.createElement(Box, { mb: "sm" },
            React.createElement(StyledTextInput, { inputType: "text", isPrivate: true, inputWidth: inputFieldWidth, labelContent: "Security question", name: questionName, "data-allytmfn": questionName, required: required, validate: validateQuestion || defaultValidateQuestion, "data-testid": "security-question" })),
        React.createElement(Box, null,
            React.createElement(StyledTextInput, { inputType: "text", isPrivate: true, inputWidth: inputFieldWidth, labelContent: "Answer", name: answerName, "data-allytmfn": answerName, required: required, validate: validateAnswer || defaultValidateAnswer, "data-testid": "security-answer" }))));
};
export default SecurityQuestionGroup;
//# sourceMappingURL=SecurityQuestionGroup.js.map